/**
 * /wireline-slickline/tickets — the Wireline & Slickline field ticket register
 * (GET /wireline-slickline/tickets, fieldiq-api#1345, WL-4).
 *
 * A ticket is drafted from its job's day sheets (job → Tickets → Create
 * ticket), so this register has no New button; it says where tickets come
 * from. The status and customer filters live in the URL. Subtotals are the API's (a WL ticket
 * carries no tax): a draft has no snapshot subtotal yet, so it reads `—` rather than a figure nobody approved,
 * and a ticket with an unpriced line carries the "Unpriced" flag (the API's
 * `unpricedCount`) — never a $0. Nothing on this page adds money up.
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, FileCheck2, Send } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWlCustomers, useWlMeta } from '../../../hooks/useWirelineSlickline';
import { useWlTickets } from '../../../hooks/useWlTickets';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wlScopeText, wlTicketCoverText, wlTicketStatusSkin, WL_TICKET_STATUS_SKIN } from '../../../lib/wireline-slickline-tickets';
import type { WlTicket, WlTicketCatalog } from '../../../types/wireline-slickline-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { WlUnpricedFlag } from './TicketBits';
import { WL_EYEBROW, WL_INPUT, WlBadge, WlChip, WlChipRow, WlEmpty, WlPanel, WlRegister, type WlColumn } from '../WlBits';

/** The figure a register row shows: the office's subtotal snapshot once signed, the priced-lines sum never (a list row has no lines). */
export const wlTicketTotalText = (t: WlTicket) => fmtCurrency(t.subtotal, t.currency);

export default function WlTicketsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const customer = searchParams.get('customer') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const setStatus = (next: string) => setParam('status', next);
  const catalog = (useWlMeta().data?.data ?? null) as WlTicketCatalog | null;
  const customers = useWlCustomers().data ?? [];
  const listQuery = useWlTickets({ status: status || undefined, customer_id: customer || undefined });
  const allQuery = useWlTickets();
  const tickets = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (t: WlTicket) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const statuses = catalog?.ticketStatuses?.length
    ? catalog.ticketStatuses
    : Object.entries(WL_TICKET_STATUS_SKIN).map(([value, s]) => ({ value, label: s.label }));

  const columns: WlColumn<WlTicket>[] = [
    { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
    {
      key: 'job',
      header: 'Job',
      render: (t) => (
        <>
          <p className="font-mono">{t.jobNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {t.wellName || '—'}
          </p>
        </>
      ),
    },
    { key: 'customer', header: 'Customer', render: (t) => t.customerName || '—' },
    { key: 'unit', header: 'Unit', render: (t) => <span className="font-mono">{t.unitNumber || '—'}</span> },
    { key: 'scope', header: 'Covers', render: (t) => wlScopeText(t) },
    { key: 'status', header: 'Status', render: (t) => <WlBadge skin={wlTicketStatusSkin(t.status)} value={t.status} /> },
    {
      key: 'total',
      header: 'Subtotal',
      align: 'right',
      render: (t) => (
        <span className="whitespace-nowrap">
          <span className="font-mono" data-testid="wl-ticket-total">{wlTicketTotalText(t)}</span>
          <WlUnpricedFlag count={t.unpricedCount} />
        </span>
      ),
    },
  ];

  return (
    <div data-testid="wl-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Field Tickets"
        description="Tickets drafted from the office-approved day sheets, approved by the office and signed by the company man."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Drafts', tone: 'neutral', loading: allQuery.isLoading, value: countOf((t) => t.status === 'draft') },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'With the customer', tone: 'accent', loading: allQuery.isLoading, value: countOf((t) => t.status === 'office_approved') },
          { icon: <FileCheck2 className="size-4" aria-hidden="true" />, label: 'Customer approved', tone: 'success', loading: allQuery.isLoading, value: countOf((t) => t.status === 'customer_approved') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            A ticket is drafted from its job: open the <Link className="underline" to="/wireline-slickline/jobs">job</Link>, then Tickets → Create ticket.
          </p>
          <WlChipRow label="Filter by status">
            <WlChip label="All" active={!status} onClick={() => setStatus('')} count={allQuery.isLoading ? undefined : countOf(() => true)} />
            {statuses.map((s) => (
              <WlChip key={s.value} label={s.label} active={status === s.value} onClick={() => setStatus(s.value)} count={allQuery.isLoading ? undefined : countOf((t) => t.status === s.value)} />
            ))}
          </WlChipRow>
          <div className="grid gap-3 sm:grid-cols-[16rem]">
            <select className={WL_INPUT} aria-label="Filter by customer" value={customer} onChange={(e) => setParam('customer', e.target.value)}>
              <option value="">Every customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <WlPanel eyebrow="Ticket register" title="Field tickets" testId="wl-ticket-register" footer={!listQuery.isLoading && tickets.length ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` : undefined}>
            <WlRegister
              query={listQuery}
              rows={tickets}
              columns={columns}
              rail={(t) => wlTicketStatusSkin(t.status).rail}
              onOpen={(t) => openRecord(`/wireline-slickline/tickets/${t.id}`)}
              subject="field tickets"
              testId="wl-ticket"
              minWidth="min-w-[940px]"
              empty={status || customer ? <WlEmpty>No ticket matches these filters.</WlEmpty> : <WlEmpty>No field tickets yet. Draft one from a job.</WlEmpty>}
              card={(t) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{t.ticketNumber || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{t.jobNumber || '—'} · {t.wellName || '—'} · {t.customerName || '—'}</p>
                    </div>
                    <WlBadge skin={wlTicketStatusSkin(t.status)} value={t.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {wlTicketCoverText(t)} · Subtotal {wlTicketTotalText(t)}
                    <WlUnpricedFlag count={t.unpricedCount} testId="wl-unpriced-flag-card" />
                  </p>
                </>
              )}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
